import React, { useCallback, useMemo } from "react";
import { Path } from "../svglib/build/svglib";

import {
    useColorModeValue
} from "@chakra-ui/react";

import { useEffect, useState } from "react";
import ReactDOMServer from "react-dom/server";

import { saveAs } from "file-saver";
import { zipSync } from "fflate";
import { STLExporter } from "three/examples/jsm/exporters/STLExporter.js";
import { BoxGeometry, Color, Mesh, MeshStandardMaterial, Scene } from "three";
import { buildBoxPanels, buildCardLeftRightWallGeometry, buildCardFrontBackWallGeometry, buildCardDividerGeometry } from "./boxModelUtils";

const MIN_WIDTH_SEG = 3;
const MIN_DEPTH_SEG = 3;
const MIN_HEIGHT_SEG = 2;

const SAVE_SVG_FILL = "#4299e1";

export const SchemeCanvas = ({ width, depth, height, thickness, isCardTray, dividers, segmentLength, kerf, hideCanvas = false, onExportHandlersReady }) => {

    const [bottomPath, setBottomPath] = useState();
    const [frontPath, setFrontPath] = useState();
    const [rightPath, setRightPath] = useState();
    const [dividerPath, setDividerPath] = useState();

    const [viewBox, setViewBox] = useState("0 0 0 0");

    const fillColor = useColorModeValue("#4299e1", "#2B6CB0");
    const exportColor = fillColor;

    useEffect(() => {

        var i, j;

        const safeWidth = Math.max(width, 0.0001);
        const safeDepth = Math.max(depth, 0.0001);
        const safeHeight = Math.max(height, 0.0001);
        const safeThickness = Math.max(thickness, 0.0001);
        const safeHeightMinusThickness = Math.max(safeHeight - safeThickness, 0.0001);
        const safeSegment = Math.max(segmentLength, 0.0001);

        var seg_w = Math.max(MIN_WIDTH_SEG, Math.floor(safeWidth / safeSegment));
        var seg_d = Math.max(MIN_DEPTH_SEG, Math.floor(safeDepth / safeSegment));
        var seg_h = Math.max(MIN_HEIGHT_SEG, Math.floor(safeHeightMinusThickness / safeSegment));

        if (seg_w % 2 === 0) seg_w += 1;
        if (seg_d % 2 === 0) seg_d += 1;

        const range = (start, stop, step) => {
            const safeStep = (Number.isFinite(step) && step > 0) ? step : 1;
            return Array.from({ length: (stop - start + 0.001) / safeStep + 1 }, (_, i) => start + (i * safeStep));
        };
        const x_coords = range(0, safeWidth, safeWidth / seg_w).slice(1, -1);
        const y_coords = range(0, safeDepth, safeDepth / seg_d).slice(1, -1);
        const z_coords = range(0, safeHeightMinusThickness, safeHeightMinusThickness / seg_h).slice(1, -1);

        const halfKerf = kerf / 2;

        var bottom_path = new Path();
        bottom_path.M(0 - halfKerf, 0 - halfKerf);
        x_coords.forEach((x, i) => {
            if (i % 2 === 0) {
                bottom_path.L(x + halfKerf, 0 - halfKerf);
                bottom_path.l(0, thickness);
            } else {
                bottom_path.L(x - halfKerf, thickness - halfKerf);
                bottom_path.l(0, -thickness);
            }
        });
        bottom_path.L(width + halfKerf, 0 - halfKerf);
        y_coords.forEach((y, i) => {
            if (i % 2 === 0) {
                bottom_path.L(width + halfKerf, y + halfKerf);
                bottom_path.l(-thickness, 0);
            }
            else {
                bottom_path.L(width - thickness + halfKerf, y - halfKerf);
                bottom_path.l(thickness, 0);
            }
        });
        bottom_path.L(width + halfKerf, depth + halfKerf);
        [...x_coords].reverse().forEach((x, i) => {
            if (i % 2 === 0) {
                bottom_path.L(x - halfKerf, depth + halfKerf);
                bottom_path.l(0, -thickness);
            }
            else {
                bottom_path.L(x + halfKerf, depth - thickness + halfKerf);
                bottom_path.l(0, thickness);
            }
        });
        bottom_path.L(0 - halfKerf, depth + halfKerf);
        [...y_coords].reverse().forEach((y, i) => {
            if (i % 2 === 0) {
                bottom_path.L(0 - halfKerf, y - halfKerf);
                bottom_path.l(thickness, 0);
            }
            else {
                bottom_path.L(thickness - halfKerf, y + halfKerf);
                bottom_path.l(-thickness, 0);
            }
        });
        bottom_path.Z();
        if (dividers > 0 && !isCardTray) {
            const dividerStep = (width - thickness) / (dividers + 1);
            for (i = 0; i < dividers; i++) {
                for (j = 0; j < y_coords.length; j++) {
                    if (j % 2 === 0) {
                        bottom_path.M((i + 1) * dividerStep + thickness - halfKerf, y_coords[j] + halfKerf);
                        bottom_path.L((i + 1) * dividerStep + thickness - halfKerf, y_coords[j + 1] - halfKerf);
                        bottom_path.l(-thickness + 2 * halfKerf, 0);
                        bottom_path.L((i + 1) * dividerStep + halfKerf, y_coords[j] + halfKerf);
                        bottom_path.Z();
                    }
                }
            }
        }
        setBottomPath(bottom_path.d());

        var front_path = new Path();
        front_path.M(0 - halfKerf, thickness - halfKerf);
        x_coords.forEach((x, i) => {
            if (i % 2 === 0) {
                front_path.L(x - halfKerf, thickness - halfKerf);
                front_path.l(0, -thickness);
            } else {
                front_path.L(x + halfKerf, 0 - halfKerf);
                front_path.l(0, thickness);
            }
        });
        front_path.L(width + halfKerf, thickness - halfKerf);
        z_coords.forEach((z, i) => {
            if (i % 2 === 0) {
                front_path.L(width + halfKerf, z + thickness + halfKerf);
                front_path.l(-thickness, 0);
            }
            else {
                front_path.L(width - thickness + halfKerf, z + thickness - halfKerf);
                front_path.l(thickness, 0);
            }
        });
        if (z_coords.length % 2 === 0) {
            front_path.L(width + halfKerf, height + halfKerf);
        } else {
            front_path.L(width - thickness + halfKerf, height + halfKerf);
        }
        if (dividers > 0) {
            const dividerStep = (width - thickness * (dividers + 2)) / (dividers + 1);
            if (isCardTray) {
                for (i = 0; i < dividers; i++) {
                    if (i === 0) {
                        front_path.L(width - (i + 1) * dividerStep - thickness / 2, height + halfKerf);
                        front_path.A(thickness / 2 + halfKerf, thickness / 2 + halfKerf, 0, 0, 1, width - (i + 1) * dividerStep - thickness - halfKerf, height - thickness / 2);
                        front_path.L(width - (i + 1) * dividerStep - thickness - halfKerf, (height + thickness) / 2 + halfKerf);
                        front_path.l(-thickness + 2 * halfKerf, 0);
                        front_path.L(width - (i + 1) * dividerStep - 2 * thickness + halfKerf, height - thickness / 2);
                        front_path.A(thickness / 2 + halfKerf, thickness / 2 + halfKerf, 0, 0, 1, width - (i + 1) * dividerStep - 2 * thickness - thickness / 2, height + halfKerf);
                    } else {
                        front_path.L(width - (i + 1) * (dividerStep + thickness) + thickness / 2 - halfKerf, height + halfKerf);
                        front_path.A(thickness / 2 + halfKerf, thickness / 2 + halfKerf, 0, 0, 1, width - (i + 1) * (dividerStep + thickness) - halfKerf, height - thickness / 2);
                        front_path.L(width - (i + 1) * (dividerStep + thickness) - halfKerf, (height + thickness) / 2 + halfKerf);
                        front_path.l(-thickness + 2 * halfKerf, 0);
                        front_path.L(width - (i + 1) * (dividerStep + thickness) - thickness + halfKerf, height - thickness / 2);
                        front_path.A(thickness / 2 + halfKerf, thickness / 2 + halfKerf, 0, 0, 1, width - (i + 1) * (dividerStep + thickness) - thickness - thickness / 2, height + halfKerf);
                    }
                }
            } else if (z_coords.length % 2 === 1) {
                for (i = 0; i < dividers; i++) {
                    front_path.L(width - (i + 1) * (dividerStep + thickness) - halfKerf, height + halfKerf);
                    front_path.l(0, -z_coords[0]);
                    front_path.l(-thickness + 2 * halfKerf, 0);
                    front_path.l(0, z_coords[0]);
                }
            }
        }
        if (z_coords.length % 2 === 0) {
            front_path.L(0 - halfKerf, height + halfKerf);
        } else {
            front_path.L(thickness - halfKerf, height + halfKerf);
        }
        [...z_coords].reverse().forEach((z, i) => {
            if (z_coords.length % 2 === 0) {
                if (i % 2 === 0) {
                    front_path.L(0 - halfKerf, z + thickness - halfKerf);
                    front_path.l(thickness, 0);
                } else {
                    front_path.L(thickness - halfKerf, z + thickness + halfKerf);
                    front_path.l(-thickness, 0);
                }
            } else {
                if (i % 2 === 0) {
                    front_path.L(thickness - halfKerf, z + thickness + halfKerf);
                    front_path.l(-thickness, 0);
                } else {
                    front_path.L(0 - halfKerf, z + thickness - halfKerf);
                    front_path.l(thickness, 0);
                }
            }
        });
        front_path.Z();
        if (dividers > 0 && !isCardTray) {
            const dividerStep = (width - thickness) / (dividers + 1);
            for (i = 0; i < dividers; i++) {
                if (z_coords.length % 2 === 0) {
                    for (j = 0; j < z_coords.length; j++) {
                        if (j % 2 === 0) {
                            front_path.M((i + 1) * dividerStep + thickness - halfKerf, z_coords[j] + thickness + halfKerf);
                            front_path.L((i + 1) * dividerStep + thickness - halfKerf, z_coords[j + 1] + thickness - halfKerf);
                            front_path.l(-thickness + 2 * halfKerf, 0);
                            front_path.L((i + 1) * dividerStep + halfKerf, z_coords[j] + thickness + halfKerf);
                            front_path.Z();
                        }
                    }
                } else if (z_coords.length >= 3) {
                    for (j = 1; j < z_coords.length - 1; j++) {
                        if (j % 2 !== 0) {
                            front_path.M((i + 1) * dividerStep + thickness - halfKerf, z_coords[j - 1] + thickness + halfKerf);
                            front_path.L((i + 1) * dividerStep + thickness - halfKerf, z_coords[j] + thickness - halfKerf);
                            front_path.l(-thickness + 2 * halfKerf, 0);
                            front_path.L((i + 1) * dividerStep + halfKerf, z_coords[j - 1] + thickness + halfKerf);
                            front_path.Z();
                        }
                    }
                }
            }
        }
        setFrontPath(front_path.d());

        var right_path = new Path();
        right_path.M(thickness - halfKerf, thickness - halfKerf);
        y_coords.forEach((y, i) => {
            if (i % 2 === 0) {
                right_path.L(thickness - halfKerf, y - halfKerf);
                right_path.l(-thickness, 0);
            } else {
                right_path.L(0 - halfKerf, y + halfKerf);
                right_path.l(thickness, 0);
            }
        });
        right_path.L(thickness - halfKerf, depth - thickness + halfKerf);
        z_coords.forEach((z, i) => {
            if (i % 2 === 0) {
                right_path.L(z + thickness - halfKerf, depth - thickness + halfKerf);
                right_path.l(0, thickness);
            } else {
                right_path.L(z + thickness + halfKerf, depth + halfKerf);
                right_path.l(0, -thickness);
            }
        });
        if (z_coords.length % 2 === 0) {
            right_path.L(height + halfKerf, depth - thickness + halfKerf);
        } else {
            right_path.L(height + halfKerf, depth + halfKerf);
        }
        if (isCardTray) {
            const curveEnd1 = -thickness + (depth + thickness) / 3;
            const curveEnd2 = thickness + depth - (depth + thickness) / 3;
            right_path.L(height + halfKerf, curveEnd2);
            right_path.A(thickness + halfKerf, thickness + halfKerf, 0, 0, 0, height - thickness, curveEnd2 - thickness - halfKerf);
            right_path.L(height - thickness * 1.5, curveEnd2 - thickness - halfKerf);
            right_path.C(height / 2 + halfKerf * 2, curveEnd2 - thickness - halfKerf, height / 2 + halfKerf * 2, curveEnd1 + thickness + halfKerf, height - thickness * 1.5, curveEnd1 + thickness + halfKerf);
            right_path.L(height - thickness, curveEnd1 + thickness + halfKerf);
            right_path.A(thickness + halfKerf, thickness + halfKerf, 0, 0, 0, height + halfKerf, curveEnd1);
        }
        if (z_coords.length % 2 === 0) {
            right_path.L(height + halfKerf, thickness - halfKerf);
        } else {
            right_path.L(height + halfKerf, 0 - halfKerf);
        }
        [...z_coords].reverse().forEach((z, i) => {
            if (z_coords.length % 2 === 0) {
                if (i % 2 === 0) {
                    right_path.L(z + thickness + halfKerf, thickness - halfKerf);
                    right_path.l(0, -thickness);
                } else {
                    right_path.L(z + thickness - halfKerf, 0 - halfKerf);
                    right_path.l(0, thickness);
                }
            } else {
                if (i % 2 === 0) {
                    right_path.L(z + thickness - halfKerf, 0 - halfKerf);
                    right_path.l(0, thickness);
                } else {
                    right_path.L(z + thickness + halfKerf, thickness - halfKerf);
                    right_path.l(0, -thickness);
                }
            }
        });
        right_path.Z();
        setRightPath(right_path.d());

        if (dividers > 0 && isCardTray) {
            const curveEnd1 = -thickness + (depth + thickness) / 3;
            const curveEnd2 = thickness + depth - (depth + thickness) / 3;
            var divider_path = new Path();
            divider_path.M(thickness * 1.5 - halfKerf, thickness - halfKerf);
            divider_path.l(0, depth - 2 * thickness + 2 * halfKerf);
            divider_path.l(-thickness + height / 2, 0);
            divider_path.l(0, thickness);
            divider_path.l(height / 2 - thickness + halfKerf * 2, 0);
            divider_path.l(0, curveEnd2 - depth - halfKerf);
            divider_path.a(thickness + halfKerf, thickness + halfKerf, 0, 0, 0, -thickness - halfKerf, -thickness - halfKerf);
            divider_path.C(height / 2 + halfKerf * 2, curveEnd2 - thickness - halfKerf, height / 2 + halfKerf * 2, curveEnd1 + thickness + halfKerf, height - thickness * 1.5, curveEnd1 + thickness + halfKerf);
            divider_path.a(thickness + halfKerf, thickness + halfKerf, 0, 0, 0, thickness + halfKerf, -thickness - halfKerf);
            divider_path.l(0, -curveEnd1 - halfKerf);
            divider_path.l(thickness - height / 2 - halfKerf * 2, 0);
            divider_path.l(0, thickness);
            divider_path.Z();
            setDividerPath(divider_path.d());
        }

        setViewBox("-5 -5 " + (width + height * 3 + 10).toString() + " " + (depth + height * 2 + 10).toString());

    }, [width, depth, height, thickness, dividers, isCardTray, segmentLength, kerf]);

    const displaySVG = useMemo(() => <svg width={(width + height * 3 + 10).toString() + "mm"} height={(depth + height * 2 + 10).toString() + "mm"} viewBox={viewBox} fillRule="evenodd">
        <path d={bottomPath} fill={fillColor} stroke="black" strokeWidth="0.2" transform={"translate(" + (height) + " " + (height) + ")"} />
        <path d={frontPath} fill={fillColor} stroke="black" strokeWidth="0.2" transform={"translate(" + (height) + " " + (height + depth + 1) + ")"} />
        <path d={frontPath} fill={fillColor} stroke="black" strokeWidth="0.2" transform={"translate(" + (height) + " " + (height - 1) + ") scale(1 -1)"} />
        <path d={rightPath} fill={fillColor} stroke="black" strokeWidth="0.2" transform={"translate(" + (height + width + 1) + " " + (height) + ")"} />
        <path d={rightPath} fill={fillColor} stroke="black" strokeWidth="0.2" transform={"translate(" + (height - 1) + " " + (height) + ") scale(-1 1)"} />
        {dividers > 0 && isCardTray && <path d={dividerPath} fill={fillColor} stroke="black" strokeWidth="0.2" transform={"translate(" + (height * 2 + width) + " " + (height) + ")"} />}
    </svg>, [bottomPath, dividerPath, dividers, fillColor, frontPath, width, height, isCardTray, rightPath, viewBox, depth]);

    const saveSvg = useMemo(() => <svg width={(width + height * 3 + 10).toString() + "mm"} height={(depth + height * 2 + 10).toString() + "mm"} viewBox={viewBox} fillRule="evenodd" >
        <path d={bottomPath} fill={SAVE_SVG_FILL} stroke="black" strokeWidth="0.2" transform={"translate(" + (height) + " " + (height) + ")"} />
        <path d={frontPath} fill={SAVE_SVG_FILL} stroke="black" strokeWidth="0.2" transform={"translate(" + (height) + " " + (height + depth + 1) + ")"} />
        <path d={frontPath} fill={SAVE_SVG_FILL} stroke="black" strokeWidth="0.2" transform={"translate(" + (height) + " " + (height - 1) + ") scale(1 -1)"} />
        <path d={rightPath} fill={SAVE_SVG_FILL} stroke="black" strokeWidth="0.2" transform={"translate(" + (height + width + 1) + " " + height + ")"} />
        <path d={rightPath} fill={SAVE_SVG_FILL} stroke="black" strokeWidth="0.2" transform={"translate(" + (height - 1) + " " + (height) + ") scale(-1 1)"} />
        {dividers > 0 && isCardTray && <path d={dividerPath} fill={SAVE_SVG_FILL} stroke="black" strokeWidth="0.2" transform={"translate(" + (height * 2 + width) + " " + (height) + ")"} />}
    </svg>, [bottomPath, dividerPath, dividers, frontPath, width, height, isCardTray, rightPath, viewBox, depth]);

    const exportStl = useCallback(() => {
        const { panels, safeWidth, safeDepth, safeHeight, safeThickness, wallHeight, isCardTray: cardTray } = buildBoxPanels({ width, depth, height, thickness, dividers, isCardTray });
        const includeDivider = cardTray && dividers > 0;
        const toUint8Array = (data) => {
            if (!data) return new Uint8Array();
            if (data instanceof ArrayBuffer) return new Uint8Array(data);
            if (ArrayBuffer.isView(data)) return new Uint8Array(data.buffer);
            if (typeof data === "string") return new TextEncoder().encode(data);
            return new Uint8Array();
        };

        const boxScene = new Scene();
        const dividerScene = new Scene();
        const material = new MeshStandardMaterial({ color: new Color(exportColor) });

        panels.forEach((panel) => {
            const geometry = new BoxGeometry(panel.size[0], panel.size[1], panel.size[2]);
            const mesh = new Mesh(geometry, material);
            mesh.position.set(panel.position[0], panel.position[1], panel.position[2]);
            boxScene.add(mesh);
        });

        if (cardTray) {
            const leftRightWallGeom = buildCardLeftRightWallGeometry(safeDepth, wallHeight, safeThickness);
            const frontBackWallGeom = buildCardFrontBackWallGeometry(safeWidth, safeHeight, safeThickness, dividers);

            const leftWall = new Mesh(leftRightWallGeom, material);
            leftWall.position.set(-safeWidth / 2 + safeThickness / 2, safeThickness + wallHeight / 2, 0);
            leftWall.rotation.y = Math.PI / 2;
            boxScene.add(leftWall);

            const rightWall = new Mesh(leftRightWallGeom.clone(), material);
            rightWall.position.set(safeWidth / 2 - safeThickness / 2, safeThickness + wallHeight / 2, 0);
            rightWall.rotation.y = -Math.PI / 2;
            boxScene.add(rightWall);

            const frontWall = new Mesh(frontBackWallGeom, material);
            frontWall.position.set(0, safeThickness + wallHeight / 2, safeDepth / 2 - safeThickness / 2);
            boxScene.add(frontWall);

            const backWall = new Mesh(frontBackWallGeom.clone(), material);
            backWall.position.set(0, safeThickness + wallHeight / 2, -safeDepth / 2 + safeThickness / 2);
            boxScene.add(backWall);

            if (includeDivider) {
                const dividerGeom = buildCardDividerGeometry(safeDepth, safeHeight, safeThickness);
                const divider = new Mesh(dividerGeom, material);
                divider.position.set(0, 0, 0);
                dividerScene.add(divider);
            }
        }

        const exporter = new STLExporter();
        boxScene.rotation.x = Math.PI / 2;
        boxScene.updateMatrixWorld(true);
        const boxArrayBuffer = exporter.parse(boxScene, { binary: true });
        const boxBytes = toUint8Array(boxArrayBuffer);
        if (includeDivider) {
            const dividerArrayBuffer = exporter.parse(dividerScene, { binary: true });
            const dividerBytes = toUint8Array(dividerArrayBuffer);
            const zipData = zipSync({
                "box.stl": boxBytes,
                "divider.stl": dividerBytes
            }, { level: 0 });
            const zipBlob = new Blob([zipData], { type: "application/zip" });
            saveAs(zipBlob, "box-and-divider.zip");
        } else {
            const boxBlob = new Blob([boxBytes], { type: "model/stl" });
            saveAs(boxBlob, "box.stl");
        }
    }, [depth, dividers, exportColor, height, isCardTray, thickness, width]);

    const openInBambu = useCallback(() => {
        const params = new URLSearchParams();
        params.set("width", width.toString());
        params.set("height", height.toString());
        params.set("depth", depth.toString());
        params.set("thickness", thickness.toString());
        params.set("dividers", dividers.toString());
        params.set("cardTray", isCardTray ? "true" : "false");
        const serviceUrl = `https://boxlib.shiky.me/generateBox?${params.toString()}`;
        // const bambuUrl = `bambustudioopen://${encodeURIComponent(serviceUrl)}`;
        window.open(serviceUrl, "_self");
    }, [depth, dividers, height, isCardTray, thickness, width]);

    useEffect(() => {
        if (onExportHandlersReady) {
            onExportHandlersReady({
                downloadSvg: () => {
                    var url = "data:image/svg+xml," + escape(ReactDOMServer.renderToStaticMarkup(saveSvg));
                    saveAs(url, "box.svg");
                },
                exportStl,
                openInBambu
            });
        }
    }, [onExportHandlersReady, saveSvg, exportStl, openInBambu]);

    return (<>
        {!hideCanvas && displaySVG}
    </>);
}
